import type { TeamMember } from "@/contexts/TeamContext";

const BASE = "/api/v1/team";

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...init,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error((err as { detail?: string }).detail ?? res.statusText);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

function mapResponse(data: Record<string, unknown>): TeamMember {
  const org_units = Array.isArray(data.org_units)
    ? (data.org_units as string[])
    : [];
  return {
    id: data.id as string,
    name: data.name as string,
    email: (data.email as string) ?? "",
    phone: (data.phone as string) ?? "",
    role: (data.role as string) ?? "",
    role_id: (data.role_id as string) ?? undefined,
    org_unit: (data.org_unit as string) ?? org_units[0] ?? "",
    title: (data.title as string) ?? "",
    avatar: (data.avatar as string) ?? "",
    bio: (data.bio as string) ?? "",
    joinDate: (data.joinDate as string) ?? "",
    status: (data.status as TeamMember["status"]) ?? "active",
  };
}

function toUpdatePayload(data: Partial<TeamMember>): Record<string, unknown> {
  const { role_id: ignoredRoleId, org_unit, ...rest } = data;
  void ignoredRoleId;
  const payload: Record<string, unknown> = { ...rest };
  if (org_unit !== undefined) {
    payload.org_units = org_unit ? [org_unit] : [];
  }
  return payload;
}

export const teamService = {
  list: (): Promise<TeamMember[]> =>
    request<{ items: Record<string, unknown>[]; total: number; page: number; limit: number }>(
      BASE
    ).then((res) => res.items.map(mapResponse)),

  get: (id: string): Promise<TeamMember> =>
    request<Record<string, unknown>>(`${BASE}/${id}`).then(mapResponse),

  update: (id: string, data: Partial<TeamMember>): Promise<TeamMember> =>
    request<Record<string, unknown>>(`${BASE}/${id}`, {
      method: "PUT",
      body: JSON.stringify(toUpdatePayload(data)),
    }).then(mapResponse),
};
